'use client'

import { useEffect, useMemo } from 'react'
import { useSearchParams } from '@/lib/next-shim'
import { Trans } from '@lingui/react/macro'
import { Link } from '@/lib/next-shim'
import { useSession } from '@/shared/ui/providers/AuthSessionProvider'
import { useRequiredParams } from '@/hooks/useRequiredParams'

import { useBooking } from '../model/bookingContext'
import { getInitialState } from '../model/bookingReducer'
import { BookingProvider } from './BookingProvider'

export function BookingPaymentCancelPage() {
  const { meetingLink } = useRequiredParams(['meetingLink'])
  const { status } = useSession()

  const isLoggedIn = status === 'authenticated'
  const initialState = useMemo(() => getInitialState(meetingLink, isLoggedIn), [isLoggedIn])

  if (!initialState) return null

  return (
    <BookingProvider initialState={initialState}>
      <PaymentCancel meetingLinkSlug={meetingLink} />
    </BookingProvider>
  )
}

function PaymentCancel({ meetingLinkSlug }: { meetingLinkSlug: string }) {
  const searchParams = useSearchParams()
  const { state, dispatch } = useBooking()

  // Get parameters from different sources with fallbacks
  const orderId = searchParams?.get('orderId') || state.orderId || ''

  // Store important values in context if they're not already there
  useEffect(() => {
    if (orderId && !state.orderId) {
      dispatch({ type: 'SET_ORDER_ID', payload: orderId })
    }
  }, [orderId, state.orderId, dispatch])

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center max-w-lg">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
          <svg className="h-8 w-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3 className="mb-1 text-lg font-semibold" data-testid="payment-cancelled-title">
          <Trans>Payment cancelled</Trans>
        </h3>
        <p className="text-gray-500 mb-6">
          <Trans>The payment has been cancelled. Please try again.</Trans>
        </p>

        <div className="flex flex-col space-y-4">
          <Link
            data-testid="return-to-payment"
            href={meetingLinkSlug ? `/meeting-links/${meetingLinkSlug}/booking/payment` : '/'}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            <Trans>Return to booking</Trans>
          </Link>

          <Link
            data-testid="payment-cancel-dashboard"
            href="/account/dashboard"
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
          >
            <Trans>Dashboard</Trans>
          </Link>
        </div>
      </div>
    </div>
  )
}
